/* istanbul ignore file */

import React, { memo, useCallback, useEffect } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { defineMessages, useIntl } from 'react-intl';
import { type ColorValue, ScrollView, StyleSheet, View } from 'react-native';
import Animated, {
  interpolate,
  useAnimatedRef,
  useAnimatedStyle,
  useScrollOffset,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { LI, UL } from '@expo/html-elements';

import { IllusEmpty_1, LoadingPlaceholder, Tabs, theme, useResponsive } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useLocationSearchHelpers } from '../../hooks/useLocationSearchHelpers';
import { usePredictions } from '../../hooks/usePredictions';
import { type LocationSearchService, type PredictionsListTab } from '../../machine';
import { LocationResultsListInfoMessageSM } from '../LocationResultsListInfoMessage';
import { LocationGroupCard, LocationPrediction } from './components';
import { useLocationSearchPredictionsTabs } from './hooks/useLocationSearchPredictionsTabs';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchPredictionsList = memo((props: Props) => {
  const { locationSearchActorRef, interactedLocationRestaurantSlug } = props;

  const { match } = useResponsive();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  const {
    formattedPredictions,
    predictionLocations,
    isFetchingPredictions,
    hasNoPredictions,
    selectPrediction,
  } = usePredictions({ locationSearchActorRef });

  const { navigateToSelectedLocation } = useLocationSearchHelpers({ locationSearchActorRef });

  // ─── Derived Data ────────────────────────────────────────────────────

  const predictionsCount = formattedPredictions.length;
  const locationsCount = predictionLocations.length;

  const hasPredictions = predictionsCount > 0;
  const hasPredictionLocations = locationsCount > 0;

  // ─── Tabs ────────────────────────────────────────────────────────────

  const { tabs, activeTabId, setActiveTabId, shouldRenderTabBar } =
    useLocationSearchPredictionsTabs({
      locationSearchActorRef,
      predictionsCount,
      locationsCount,
    });

  // ─── Refs ────────────────────────────────────────────────────

  const scrollContainerAnimatedRef = useAnimatedRef<Animated.ScrollView>();

  // ─── Telemetry ───────────────────────────────────────────────

  const onLocationPredictionPress = useCallback(
    (id: string, predictionAddress: string) => {
      track('location.search_prediction.press', { predictionAddress });
      selectPrediction(id);
    },
    [selectPrediction, track],
  );

  const onLocationCardPress = useCallback<OnCardPress>(
    (location) => {
      track('location.search_prediction.location-press', {
        restaurantName: location?.restaurantName,
      });

      navigateToSelectedLocation(location);
    },
    [navigateToSelectedLocation, track],
  );

  // ─── Animations ──────────────────────────────────────────────

  const scrollOffset = useScrollOffset(scrollContainerAnimatedRef);
  const loadingSV = useSharedValue(0);

  const toggleLoadingSV = useCallback(() => {
    loadingSV.value = withTiming(isFetchingPredictions ? 1 : 0, { duration: theme.transitions.base }); // prettier-ignore
  }, [isFetchingPredictions, loadingSV]);

  // ─── Styles ──────────────────────────────────────────────────

  const scrollShadowAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: interpolate(scrollOffset.value, [0, 20], [0, 1]) };
  });

  const contentAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: interpolate(loadingSV.value, [1, 0], [0, 1]) };
  });

  const loadingContainerAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: interpolate(loadingSV.value, [1, 0], [1, 0]) };
  });

  const outerContainerStyles = match([styles.outerContainerXS, styles.outerContainerSM]); // prettier-ignore
  const containerStyles = match([styles.containerXS, styles.containerSM]);
  const contentContainerStyles = match([styles.contentContainerXS, styles.contentContainerSM]); // prettier-ignore

  const loadingContainerStyles = [
    styles.loadingContainer,
    match([styles.loadingContainerXS, styles.loadingContainerSM]),
    loadingContainerAnimatedStyles,
  ];

  const innerContainerStyles = [styles.innerContentContainer, contentAnimatedStyles];

  // ─── Effects ─────────────────────────────────────────────────

  useEffect(toggleLoadingSV, [toggleLoadingSV]);

  // ─────────────────────────────────────────────────────────────────────

  if (hasNoPredictions) {
    return (
      <View style={outerContainerStyles}>
        <ScrollView style={containerStyles} contentContainerStyle={contentContainerStyles}>
          <LocationResultsListInfoMessageSM
            title={formatMessage(messages.noResultsTitle)}
            text={formatMessage(messages.noResultsText)}
            illustration={IllusEmpty_1}
            illustrationAriaLabel="No results"
          />
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={outerContainerStyles}>
      <Animated.View
        style={[styles.shadowContainer, scrollShadowAnimatedStyles]}
        pointerEvents="none"
      >
        <LinearGradient
          locations={SHADOW_GRADIENT_LOCATIONS}
          colors={SHADOW_GRADIENT_COLORS}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>

      <Animated.ScrollView
        ref={scrollContainerAnimatedRef}
        style={containerStyles}
        contentContainerStyle={contentContainerStyles}
      >
        <Animated.View style={loadingContainerStyles} pointerEvents="none">
          <LoadingPlaceholder rows={5} rowHeight={44} palette="cream" />
        </Animated.View>

        <Animated.View style={innerContainerStyles}>
          {shouldRenderTabBar ? (
            <Tabs.ButtonBar<PredictionsListTab>
              tabs={tabs}
              activeTabId={activeTabId}
              onChange={setActiveTabId}
              isFluid
              style={styles.tabButtonBar}
            />
          ) : null}

          {tabs.map((tab) => {
            const isPredictionsTab = tab.id === 'location-prediction-tab.predictions'; // prettier-ignore
            const isLocationsTab = tab.id === 'location-prediction-tab.locations'; // prettier-ignore

            if (isPredictionsTab && hasPredictions) {
              return (
                <Tabs.Panel
                  key={tab.id}
                  isActive={activeTabId === tab.id}
                  tabId={tab.id}
                  panelId={tab.panelId}
                  useView
                >
                  <UL>
                    {formattedPredictions.map((prediction) => (
                      <LI key={prediction.id}>
                        <LocationPrediction
                          prediction={prediction}
                          onPress={onLocationPredictionPress}
                          withoutIcon
                        />
                      </LI>
                    ))}
                  </UL>
                </Tabs.Panel>
              );
            }

            if (isLocationsTab && hasPredictionLocations) {
              return (
                <Tabs.Panel
                  key={tab.id}
                  isActive={activeTabId === tab.id}
                  tabId={tab.id}
                  panelId={tab.panelId}
                  useView
                >
                  <UL>
                    {predictionLocations.map((location) => (
                      <LI key={location.id} style={styles.cardsListItem}>
                        <LocationGroupCard
                          locationSearchActorRef={locationSearchActorRef}
                          location={location}
                          isLoading={isFetchingPredictions}
                          onPress={onLocationCardPress}
                          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug} // prettier-ignore
                        />
                      </LI>
                    ))}
                  </UL>
                </Tabs.Panel>
              );
            }
          })}
        </Animated.View>
      </Animated.ScrollView>
    </View>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locations: {
    defaultMessage: 'Locations ({count})',
    description: 'Location Search | Predictions | Locations title',
  },
  sgRestaurants: {
    defaultMessage: 'SG Restaurants ({count})',
    description: 'Location Search | Predictions | SG Restaurants title',
  },
  noResultsTitle: {
    defaultMessage: "Ah snap peas! We couldn't find any locations.",
    description: 'Location Search | Predictions | No results title',
  },
  noResultsText: {
    defaultMessage: 'Try another search',
    description: 'Location Search | Predictions | No results text',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const SHADOW_GRADIENT_LOCATIONS = [0, 1] as const;
const SHADOW_GRADIENT_COLORS: [ColorValue, ColorValue] = [
  theme.colors.CREAM,
  theme.colors.OPACITY.CREAM.TRANSPARENT,
] as const;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainerXS: {
    ...StyleSheet.absoluteFill,
  },
  outerContainerSM: {
    flex: 1,
  },
  shadowContainer: {
    ...StyleSheet.absoluteFill,
    zIndex: 2,
    height: 80,
  },
  containerXS: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    paddingHorizontal: theme.spacing['2'],
  },
  containerSM: {
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['2'],
  },
  contentContainerXS: {
    paddingVertical: theme.spacing['7'],
  },
  contentContainerSM: {
    paddingBottom: theme.spacing['2'],
  },
  loadingContainer: {
    ...StyleSheet.absoluteFill,
    zIndex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    minHeight: 300,
    paddingHorizontal: theme.spacing['3'],
  },
  loadingContainerXS: {
    paddingTop: theme.spacing['7'],
  },
  loadingContainerSM: {
    paddingTop: theme.spacing['3'],
  },
  innerContentContainer: {
    gap: theme.spacing['5'],
  },
  cardsListItem: {
    paddingHorizontal: theme.spacing['2'],
  },
  tabButtonBar: {
    gap: theme.spacing['2'],
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  locationSearchActorRef: LocationSearchService; // prettier-ignore
  interactedLocationRestaurantSlug?: string;
};

type OnCardPress = (location: {
  restaurantId?: string;
  restaurantSlug?: string;
  restaurantName?: string;
}) => void;
